/**
 * API client for AutoMusic backend
 */

import { authenticatedFetch } from './auth';

export const spotifyApi = {
  playback: {
    getCurrentTrack: async () => {
      const response = await authenticatedFetch('/api/spotify/currently-playing');
      if (!response.ok) throw new Error('Failed to fetch current track');
      return response.json();
    },

    play: async () => {
      const response = await authenticatedFetch('/api/spotify/player/play', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      if (!response.ok) throw new Error('Failed to play');
      return response.json();
    },

    pause: async () => {
      const response = await authenticatedFetch('/api/spotify/player/pause', { method: 'POST' });
      if (!response.ok) throw new Error('Failed to pause');
      return response.json();
    },

    next: async () => {
      const response = await authenticatedFetch('/api/spotify/player/next', { method: 'POST' });
      if (!response.ok) throw new Error('Failed to skip to next');
      return response.json();
    },

    previous: async () => {
      const response = await authenticatedFetch('/api/spotify/player/previous', { method: 'POST' });
      if (!response.ok) throw new Error('Failed to skip to previous');
      return response.json();
    },

    setMood: async (mood: string) => {
      const response = await authenticatedFetch('/api/spotify/mood', {
        method: 'POST',
        body: JSON.stringify({ mood }),
      });
      if (!response.ok) throw new Error('Failed to set mood');
      return response.json();
    },
  },

  devices: {
    getDevices: async () => {
      const response = await authenticatedFetch('/api/spotify/devices');
      if (!response.ok) throw new Error('Failed to fetch devices');
      return response.json();
    },

    setDevice: async (deviceId: string) => {
      const response = await authenticatedFetch('/api/spotify/device', {
        method: 'POST',
        body: JSON.stringify({ device_id: deviceId }),
      });
      if (!response.ok) throw new Error('Failed to set device');
      return response.json();
    },

    transferPlayback: async (deviceId: string, play: boolean = true) => {
      const response = await authenticatedFetch('/api/spotify/transfer-playback', {
        method: 'POST',
        body: JSON.stringify({ device_id: deviceId, play }),
      });
      if (!response.ok) throw new Error('Failed to transfer playback');
      return response.json();
    },
  },

  playlists: {
    getPlaylists: async () => {
      const response = await authenticatedFetch('/api/spotify/playlists');
      if (!response.ok) throw new Error('Failed to fetch playlists');
      return response.json();
    },
  },

  ratings: {
    rateTrack: async (trackId: string, rating: 'like' | 'dislike') => {
      const response = await authenticatedFetch('/api/ratings/rate', {
        method: 'POST',
        body: JSON.stringify({ track_id: trackId, rating }),
      });
      if (!response.ok) throw new Error('Failed to rate track');
      return response.json();
    },

    submitRating: async (ratingData: { track_id: string; rating: number }) => {
      const response = await authenticatedFetch('/api/ratings/rate', {
        method: 'POST',
        body: JSON.stringify(ratingData),
      });
      if (!response.ok) throw new Error('Failed to submit rating');
      return response.json();
    },

    getRatings: async () => {
      const response = await authenticatedFetch('/api/ratings');
      if (!response.ok) throw new Error('Failed to fetch ratings');
      return response.json();
    },
  },

  mood: {
    getMoodRecommendations: async (mood: string, limit: number = 10) => {
      const response = await authenticatedFetch(`/api/recommendations/mood/${mood}?limit=${limit}`);
      if (!response.ok) throw new Error('Failed to get mood recommendations');
      return response.json();
    },

    generatePlaylist: async (mood: string) => {
      const response = await authenticatedFetch('/api/spotify/generate-playlist', {
        method: 'POST',
        body: JSON.stringify({ mood }),
      });
      if (!response.ok) throw new Error('Failed to generate playlist');
      return response.json();
    },
  },

  chat: {
    sendMessage: async (message: string) => {
      const response = await authenticatedFetch('/api/chat/message', {
        method: 'POST',
        body: JSON.stringify({ message }),
      });
      if (!response.ok) throw new Error('Failed to send message');
      return response.json();
    },

    getHistory: async (limit?: number) => {
      const url = limit ? `/api/chat/history?limit=${limit}` : '/api/chat/history';
      const response = await authenticatedFetch(url);
      if (!response.ok) throw new Error('Failed to fetch chat history');
      return response.json();
    },

    clearHistory: async () => {
      const response = await authenticatedFetch('/api/chat/clear', {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to clear chat history');
      return response.json();
    },
  },

  tracks: {
    play: async (uri: string) => {
      const response = await authenticatedFetch('/api/spotify/player/play', {
        method: 'POST',
        body: JSON.stringify({ uris: [uri] }),
      });
      if (!response.ok) throw new Error('Failed to play track');
      return response.json();
    },

    playMultiple: async (uris: string[]) => {
      const response = await authenticatedFetch('/api/spotify/player/play', {
        method: 'POST',
        body: JSON.stringify({ uris }),
      });
      if (!response.ok) throw new Error('Failed to play tracks');
      return response.json();
    },

    addToQueue: async (uri: string) => {
      const response = await authenticatedFetch('/api/spotify/player/queue', {
        method: 'POST',
        body: JSON.stringify({ uri }),
      });
      if (!response.ok) throw new Error('Failed to add to queue');
      return response.json();
    },

    saveToPlaylist: async (trackId: string, playlistId?: string) => {
      const response = await authenticatedFetch('/api/spotify/save-track', {
        method: 'POST',
        body: JSON.stringify({ track_id: trackId, playlist_id: playlistId }),
      });
      if (!response.ok) throw new Error('Failed to save track');
      return response.json();
    },
  },
};
